/**
 * Hook for managing lesson listing pages
 * Combines data fetching, mutations, navigation, and permission checks
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useConfirmationDialog } from '@/shared/hooks/useConfirmationDialog';
import { useLessonsQuery as useLessonsByType } from './useLessonsQueries';
import { useLessonMutations } from './use-lesson-mutations';
import { getLessonTypeConfig, hasPermission } from '../config/lesson-types.config';
import { LessonType, Role } from '../core/types';
import type { LessonDTO } from '../core/types/lesson.types';
import { practicalPaths } from '../features/practical/practical-evidence';

interface UseLessonListingOptions {
  lessonType: LessonType;
  userRole: Role;
  showHidden?: boolean;
}

interface UseLessonListingReturn {
  // Data
  lessons: LessonDTO[];
  config: ReturnType<typeof getLessonTypeConfig>;

  // Loading states
  isLoading: boolean;
  isRefreshing: boolean;
  isMutating: boolean;
  /** Un 500/retea la lista ramane distinct de „zero lectii" — vezi LessonListingPage. */
  isError: boolean;

  // Actions
  createLesson: () => void;
  editLesson: (lessonId: string) => void;
  startCompletion: (lessonId: string) => void;
  previewLesson: (lessonId: string) => void;
  /** B3: pagina „Detalii" a activitatii practice. */
  openDetails: (lessonId: string) => void;
  /** B3: ecranul „Încarcă poze" (doar PROFESOR — butonul apare doar la el). */
  openUpload: (lessonId: string) => void;
  toggleVisibility: (lessonId: string, currentlyHidden: boolean) => void;
  duplicateLesson: (lessonId: string) => void;
  /** Cere stergerea: DESCHIDE dialogul de confirmare, nu sterge. Stergerea propriu-zisa
   *  se face din `deleteConfirmation.handleConfirm`, dupa raspunsul utilizatorului. */
  deleteLesson: (lessonId: string) => void;
  /** Starea dialogului de confirmare a stergerii. Consumatorul TREBUIE sa randeze
   *  `<ConfirmationDialog>` cu ea, altfel stergerea nu se poate confirma niciodata. */
  deleteConfirmation: {
    isOpen: boolean;
    handleConfirm: () => void | Promise<void>;
    handleCancel: () => void;
  };
  refresh: () => void;

  // Permissions
  canCreate: boolean;
  canEdit: boolean;
  canComplete: boolean;
  canToggleVisibility: boolean;
  canDelete: boolean;

  // Helpers
  getLessonPath: (action: 'edit' | 'complete' | 'preview', lessonId?: string) => string;
}

/**
 * Hook for lesson listing functionality
 * Provides data, actions, and permissions for a specific lesson type
 *
 * @example
 * const listing = useLessonListing({
 *   lessonType: LessonType.LESSON,
 *   userRole: Role.ADMIN
 * });
 *
 * // Display lessons
 * listing.lessons.map(lesson => ...)
 *
 * // Create new lesson
 * <button onClick={listing.createLesson}>
 *
 * // Toggle visibility
 * listing.toggleVisibility(lesson.id, lesson.isHidden)
 */
export function useLessonListing({
  lessonType,
  userRole,
  showHidden = true,
}: UseLessonListingOptions): UseLessonListingReturn {
  const navigate = useNavigate();
  const config = getLessonTypeConfig(lessonType);

  // Fetch lessons
  const filters = showHidden ? {} : { isHidden: false };
  const {
    data: lessons = [],
    isLoading,
    isFetching,
    isError,
    refetch
  } = useLessonsByType(lessonType, filters);

  // Mutations
  const mutations = useLessonMutations();

  // ============================================================================
  // PERMISSIONS
  // ============================================================================

  const canCreate = hasPermission(userRole, lessonType, 'create');
  const canEdit = hasPermission(userRole, lessonType, 'edit');
  const canComplete = hasPermission(userRole, lessonType, 'complete');
  const canToggleVisibility = hasPermission(userRole, lessonType, 'edit'); // Same as edit
  const canDelete = hasPermission(userRole, lessonType, 'edit'); // Same as edit

  // ============================================================================
  // PATH HELPERS
  // ============================================================================

  /**
   * Get the URL path for a lesson action
   */
  const getLessonPath = (
    action: 'edit' | 'complete' | 'preview',
    lessonId?: string
  ): string => {
    // Base paths by lesson type
    const basePaths: Record<LessonType, Record<string, string>> = {
      [LessonType.LESSON]: {
        list: '/lectii',
        edit: '/lectii/editare',
        complete: '/completare-lectie/id',
        preview: '/completare-lectie/id',
      },
      [LessonType.HOMEWORK]: {
        list: '/exercitii-pentru-acasa',
        edit: '/exercitii-pentru-acasa/editare',
        complete: '/completare-exercitiu-pentru-acasa/id',
        preview: '/completare-exercitiu-pentru-acasa/id',
      },
      [LessonType.PRACTICAL_ACTIVITY]: {
        list: '/activitati-practice',
        edit: '/activitati-practice/editare',
        complete: '/completare-activitate-practica/id',
        preview: '/completare-activitate-practica/id',
      },
      [LessonType.MONTHLY_CHALLENGE]: {
        list: '/provocari',
        edit: '/provocari/editare',
        complete: '/completare-provocare/id',
        preview: '/completare-provocare/id',
      },
      [LessonType.CLASS_EXERCISE]: {
        list: '/exercitii-pentru-clasa',
        edit: '/exercitii-pentru-clasa/editare',
        complete: '/completare-exercitiu-pentru-clasa/id',
        preview: '/completare-exercitiu-pentru-clasa/id',
      },
      // Provocarea pentru scoala nu trece prin player: n-are rute de completare/previzualizare.
      // Cardul ei foloseste „Detalii" si „Încarcă dovezi" (practicalPaths).
      [LessonType.SCHOOL_CHALLENGE]: {
        list: '/provocari-scoli',
        edit: '/provocari-scoli/editare',
        complete: '/provocari-scoli/id',
        preview: '/provocari-scoli/id',
      },
      [LessonType.DAILY_CHALLENGE]: {
        list: '/provocari-zilnice',
        edit: '/provocari-zilnice/editare',
        complete: '/completare-provocare-zilnica/id',
        preview: '/completare-provocare-zilnica/id',
      },
    };

    const paths = basePaths[lessonType];

    if (action === 'edit') {
      return lessonId ? `${paths.edit}/${lessonId}` : paths.edit;
    }

    if (action === 'complete') {
      return lessonId ? `${paths.complete}/${lessonId}` : paths.complete;
    }

    if (action === 'preview') {
      return lessonId ? `${paths.preview}/${lessonId}/preview` : paths.preview;
    }

    return paths.list;
  };

  // ============================================================================
  // NAVIGATION ACTIONS
  // ============================================================================

  /**
   * Navigate to create new lesson page
   */
  const createLesson = () => {
    if (!canCreate) {
      console.warn('User does not have permission to create lessons');
      return;
    }
    navigate(getLessonPath('edit'));
  };

  /**
   * Navigate to edit lesson page
   */
  const editLesson = (lessonId: string) => {
    if (!canEdit) {
      console.warn('User does not have permission to edit lessons');
      return;
    }
    navigate(getLessonPath('edit', lessonId));
  };

  /**
   * Navigate to lesson completion page
   */
  const startCompletion = (lessonId: string) => {
    if (!canComplete) {
      console.warn('User does not have permission to complete lessons');
      return;
    }
    navigate(getLessonPath('complete', lessonId));
  };

  /**
   * Navigate to lesson preview (completion in preview mode)
   */
  const previewLesson = (lessonId: string) => {
    navigate(getLessonPath('preview', lessonId));
  };

  /** B3: „Detalii" - pagina de citit a activitatii practice. */
  const openDetails = (lessonId: string) => {
    navigate(practicalPaths(lessonId, lessonType).detalii);
  };

  /** B3: „Încarcă poze" - depunerea intr-un singur ecran, fara parcursul de lectie. */
  const openUpload = (lessonId: string) => {
    navigate(practicalPaths(lessonId, lessonType).incarca);
  };

  // ============================================================================
  // MUTATION ACTIONS
  // ============================================================================

  /**
   * Toggle lesson visibility (show/hide)
   */
  const toggleVisibility = (lessonId: string, currentlyHidden: boolean) => {
    if (!canToggleVisibility) {
      console.warn('User does not have permission to toggle visibility');
      return;
    }

    mutations.toggleVisibility.mutate({
      id: lessonId,
      isHidden: !currentlyHidden,
    });
  };

  /**
   * Duplicate a lesson (creates a copy)
   */
  const duplicateLesson = (lessonId: string) => {
    if (!canCreate) {
      console.warn('User does not have permission to duplicate lessons');
      return;
    }

    mutations.duplicate.mutate(lessonId);
  };

  // `confirm()` global (fara `window.`) inlocuit cu dialogul aplicatiei: caseta nativa nu
  // respecta tokenii si pragul de 44px, blocheaza firul de executie (deci si canalul CDP,
  // ceea ce ingheata orice harness de masurare) si nu exista in DOM, deci nu se poate
  // verifica pe ecran. Fiind un hook, nu poate randa el dialogul — expune starea, iar
  // consumatorul o randeaza.
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);

  const deleteConfirmation = useConfirmationDialog({
    onConfirm: () => {
      if (pendingDeleteId) mutations.delete.mutate(pendingDeleteId);
      setPendingDeleteId(null);
    },
    onCancel: () => setPendingDeleteId(null),
  });

  /**
   * Request deletion of a lesson — opens the confirmation dialog.
   */
  const deleteLesson = (lessonId: string) => {
    if (!canDelete) {
      console.warn('User does not have permission to delete lessons');
      return;
    }

    setPendingDeleteId(lessonId);
    deleteConfirmation.show();
  };

  /**
   * Refresh lesson list
   */
  const refresh = () => {
    refetch();
  };

  // ============================================================================
  // RETURN
  // ============================================================================

  return {
    // Data
    lessons,
    config,

    // Loading states
    isLoading,
    isRefreshing: isFetching && !isLoading,
    isMutating: mutations.isLoading,
    isError,

    // Actions
    createLesson,
    editLesson,
    startCompletion,
    previewLesson,
    openDetails,
    openUpload,
    toggleVisibility,
    duplicateLesson,
    deleteLesson,
    deleteConfirmation,
    refresh,

    // Permissions
    canCreate,
    canEdit,
    canComplete,
    canToggleVisibility,
    canDelete,

    // Helpers
    getLessonPath,
  };
}
